import React from 'react';

import type { MenuProps } from '@mui/material';
import { Menu, MenuItem } from '@mui/material';
import { useConfirmCommentPrivacyChanges } from 'src/components/comments/commentPrivacyChange';
import {
  useEditorTabStateActions,
  useEditorTabStateSelector,
} from 'src/editor/editor-tab-state-registry';
import { useSelectCurrentUser } from 'src/gql';
import { Apollo } from 'src/gql/generated';
import { useAuthorizedTransforms, useDeleteCommentReply, useGetCurrentDoc } from 'src/hooks';
import { getIsUserInDocOwnerOrg } from 'src/hooks/document';
import { useMaybeEditor } from 'src/layout-engine';
import { useEditingMode } from 'src/store/document/selectors';

import type { CommentPanelComment } from './CommentsDrawer';

interface CommentBoxMenuProps extends MenuProps {
  commentOrReply: CommentPanelComment;
  isComment: boolean;
  isRootCommentResolved: boolean;
  rootCommentId: string;
  setEditing: React.Dispatch<React.SetStateAction<boolean>>;
  setMenuOpen: React.Dispatch<React.SetStateAction<boolean>>;
  threadIsPrivate: boolean;
}

export function CommentBoxMenu(props: CommentBoxMenuProps): JSX.Element {
  const {
    commentOrReply,
    isComment,
    isRootCommentResolved,
    rootCommentId,
    setEditing,
    setMenuOpen,
    threadIsPrivate,
    ...rest
  } = props;

  const editor = useMaybeEditor();
  const {
    comments: { setActivePanel, setCommentThreadId },
  } = useEditorTabStateActions();
  const currentUser = useSelectCurrentUser();
  const { document } = useGetCurrentDoc();
  const { editingMode, viewing } = useEditingMode();
  const { deleteCommentReply } = useDeleteCommentReply();
  const [updateComment] = Apollo.useUpdateCommentMutation();
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const { confirmationDialog, requestThreadPrivacyChange } =
    useConfirmCommentPrivacyChanges(rootCommentId);

  const activePanel = useEditorTabStateSelector(state => state.comments.activePanel);
  const canCreatePrivateComment = getIsUserInDocOwnerOrg(currentUser, document);
  const canViewPrivateCommentThread = !threadIsPrivate || canCreatePrivateComment;
  const showPrivacyMenuItem = isComment && canCreatePrivateComment && activePanel !== 'ViewThread';
  const showReply =
    canViewPrivateCommentThread &&
    isComment &&
    activePanel !== 'ViewThread' &&
    !isRootCommentResolved;

  return (
    <>
      <Menu
        anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        onClose={(): void => setMenuOpen(false)}
        {...rest}
      >
        {!isRootCommentResolved && canViewPrivateCommentThread && (
          <MenuItem
            dense
            disabled={viewing}
            onClick={(): void => {
              setEditing(true);
              setMenuOpen(false);
            }}
          >
            Edit
          </MenuItem>
        )}
        {showPrivacyMenuItem && (
          <MenuItem
            dense
            disabled={viewing}
            onClick={(): void => {
              setMenuOpen(false);
              requestThreadPrivacyChange(!commentOrReply.isPrivate);
            }}
          >
            {commentOrReply.isPrivate ? 'Make public' : 'Make private'}
          </MenuItem>
        )}
        {showReply && (
          <MenuItem
            dense
            disabled={viewing}
            onClick={(): void => {
              setCommentThreadId(rootCommentId);
              setActivePanel('ViewThread');
              setMenuOpen(false);
            }}
          >
            Reply
          </MenuItem>
        )}
        {isComment && canViewPrivateCommentThread && (
          <MenuItem
            dense
            disabled={viewing}
            onClick={async () => {
              setMenuOpen(false);

              await updateComment({
                variables: {
                  input: { id: commentOrReply.id, props: { resolved: !isRootCommentResolved } },
                },
              });
            }}
          >
            {isRootCommentResolved ? 'Reopen' : 'Resolve'}
          </MenuItem>
        )}
        {canViewPrivateCommentThread && (
          <MenuItem
            dense
            disabled={viewing}
            onClick={async (): Promise<void> => {
              if (!document || !editingMode) return;

              /**
               * If the comment is in the JSON, we simply need to remove it from the JSON and the UI will update.  No need
               * to delete it in the DB as the client filters out comments that are not in the JSON.
               */
              if (commentOrReply.nodeRefId && editor)
                AuthorizedTransforms.deleteComment(editor, {
                  editingMode,
                  nodeRefId: commentOrReply.nodeRefId,
                });

              if (isComment) {
                setActivePanel('ViewComments');
              } else {
                // The comment is a reply and we can delete it from the DB.
                await deleteCommentReply(rootCommentId, commentOrReply.id, {
                  variables: { input: { id: commentOrReply.id } },
                });
              }

              setMenuOpen(false);
            }}
          >
            Delete
          </MenuItem>
        )}
      </Menu>
      {confirmationDialog}
    </>
  );
}
